import { OFFLINE_STORES, getOfflineStorageDriver } from './offlineStorageAdapter.js';
import { createOfflineFileVault } from './offlineFileVault.js';

const positive = value => Number.isSafeInteger(value) && value > 0;
const metadataKeys = { instanceRef: 'instance_ref', instanceLabel: 'instance_label', gpsLat: 'gps_lat', gpsLng: 'gps_lng', gpsAccuracy: 'gps_accuracy', gpsCapturedAt: 'gps_captured_at' };
export function normalizeOfflineAttachmentMetadata(metadata = {}) {
    const result = {};
    for (const [key, alias] of Object.entries(metadataKeys)) {
        if (metadata[key] !== undefined && metadata[alias] !== undefined && metadata[key] !== metadata[alias]) throw Error('Los metadatos del archivo son contradictorios.');
        result[key] = metadata[key] ?? metadata[alias] ?? (key.startsWith('instance') ? '' : null);
    }
    return result;
}
const review = () => { throw Error('El archivo requiere revisar su cuenta, empresa o formulario. Los datos se conservan.'); };

// El transporte se inyecta: nunca toma credenciales del journal ni admite un ACK
// booleano. Se usa el mismo circuito con SQLite/Filesystem nativos e IndexedDB.
export function createOfflineAttachmentQueue({ fs, directory = 'DATA', driver = getOfflineStorageDriver() } = {}) {
    const vault = createOfflineFileVault({ fs, directory, driver });
    return {
        async enqueue({ userId, empresaId, visitaId, file, descripcion = '', metadata = {}, originRevision = null, kind = 'image' }) {
            if (!positive(userId) || !positive(empresaId) || !positive(visitaId) || !['image', 'document'].includes(kind)) review();
            const allowed = kind === 'image' ? ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/avif'] : ['application/pdf', 'image/jpeg', 'image/png'];
            if (!file || !allowed.includes(file.type) || !file.name || /[\x00-\x1f/\\]/.test(file.name) || file.name.length > 255
                || !Number.isSafeInteger(file.size) || file.size < 1 || file.size > (kind === 'image' ? 5 : 20) * 1024 * 1024) review();
            metadata = kind === 'image' ? normalizeOfflineAttachmentMetadata(metadata) : metadata;
            if (metadata.instanceRef && !/^[a-f0-9]{64}$/.test(originRevision || '')) review();
            const id = crypto.randomUUID();
            await vault.stage({ id, userId, empresaId, visitaId, originRevision, file, kind, intent: { storeName: OFFLINE_STORES.MEDIA,
                entry: { id, userId, visitaId, filename: file.name, mimeType: file.type, descripcion, metadata } } });
            return vault.publish(id);
        },
        async send({ entry, userId, empresaId, isCurrent, getRevisions, upload }) {
            const current = () => { if (typeof isCurrent !== 'function' || isCurrent() !== true) review(); };
            current();
            const row = await driver.get(OFFLINE_STORES.FILES, entry.id);
            if (!positive(empresaId) || row?.userId !== userId || row?.empresaId !== empresaId || row.visitaId !== entry.visitaId) review();
            // Un corte después del recibo termina el retiro por CAS sin más HTTP.
            if (row.state === 'acknowledged') {
                await vault.acknowledgeUpload({ id: row.id, userId, empresaId, isCurrent, commandSha256: row.uploadCommand?.manifestSha256, response: row.uploadReceipt });
                return { success: true, acknowledged: true, recovered: true, receipt: row.uploadReceipt.data, manifest: JSON.parse(row.uploadCommand.manifest) };
            }
            const draft=await driver.get(OFFLINE_STORES.ENTRIES,`${userId}:${entry.visitaId}`);
            if(draft?.payload?._merge)return {success:false,blocked:true,message:'La evidencia sigue guardada. Primero revisá y resolvé la propuesta de respuestas.'};
            if (typeof upload?.prepare === 'function') {
                const blocked = await upload.prepare({ visitaId: entry.visitaId, queueEntry: entry, metadata: entry.metadata,
                    attachmentCommand: row.uploadCommand || null, attachmentKind: row.kind });
                current();
                if (blocked) return blocked;
            }
            let context = {};
            if (!row.uploadCommand && entry.metadata?.instanceRef) {
                if (typeof getRevisions !== 'function') review();
                const data = await getRevisions(entry.visitaId);
                current();
                const revision = data?.visitas?.find(item => item.visita_id === entry.visitaId);
                if (data?.schema_version !== 1 || data.user_id !== userId || data.empresa_id !== empresaId
                    || data.visitas?.length !== 1 || data.unavailable_ids?.length !== 0 || !revision
                    || revision.form_revision !== row.originRevision || !Number.isSafeInteger(revision.responses_version) || revision.responses_version < 0) review();
                context = { expectedVersion: revision.responses_version, formRevision: revision.form_revision };
            }
            const command = await vault.freezeUpload({ id: row.id, userId, empresaId, isCurrent, ...context });
            const bytes = await vault.verify(await driver.get(OFFLINE_STORES.FILES, row.id));
            current();
            const file = new File([bytes], entry.filename, { type: entry.mimeType });
            const response = await upload({ visitaId: entry.visitaId, file, descripcion: entry.descripcion, metadata: entry.metadata, queueEntry: entry, attachmentCommand: command, attachmentKind: row.kind });
            current();
            if (response?.unauthorized === true) return response;
            await vault.acknowledgeUpload({ id: row.id, userId, empresaId, commandSha256: command.manifestSha256, response, isCurrent });
            return { success: true, acknowledged: true, receipt: response.data, manifest: JSON.parse(command.manifest) };
        },
        recover: options => vault.recover(options),
    };
}
